/**
 * Контекст для блока дашборда.
 * 
 * Предоставляет единый источник данных для компонентов внутри блока:
 * - ReportTable
 * - ReportChart
 * - Card (number)
 * 
 * Решает проблему несогласованности источников данных между
 * модальными окнами и компонентами отчётов.
 */

import React, { createContext, useContext, useMemo, ReactNode } from 'react';

// ==================== ТИПЫ ====================

export type BlockContextMode = 
    | 'dashboard'      // Блок на дашборде (compact режим)
    | 'modal-add'      // Модальное окно добавления
    | 'modal-edit'     // Модальное окно редактирования
    | 'modal-copy'     // Модальное окно копирования
    | 'modal-view';    // Модальное окно просмотра (FullReportModal)

export interface DashboardBlockContextValue {
    /** ID блока (null для модальных окон add) */
    blockId: string | null;
    /** Режим работы контекста */
    mode: BlockContextMode;
    /** Флаг: это модальное окно */
    isModal: boolean;
    /** Флаг: это блок на дашборде */
    isDashboardBlock: boolean;
    /** Флаг: это модальное окно редактирования/добавления/копирования */
    isEditingModal: boolean;
    /** Флаг: это модальное окно просмотра полного отчёта */
    isViewModal: boolean;
    /** Дата блока (для определения типа отчёта) */
    date?: string;
    /** Тип компонента блока */
    componentType?: string;
}

// ==================== КОНТЕКСТ ====================

const DashboardBlockContext = createContext<DashboardBlockContextValue | null>(null);

// ==================== ПРОВАЙДЕР ====================

export interface DashboardBlockProviderProps {
    children: ReactNode;
    /** ID блока */
    blockId?: string | null;
    /** Режим работы */
    mode: BlockContextMode;
    /** Дата блока */
    date?: string;
    /** Тип компонента */
    componentType?: string;
}

export const DashboardBlockProvider: React.FC<DashboardBlockProviderProps> = ({
    children,
    blockId = null,
    mode,
    date,
    componentType,
}) => {
    const value = useMemo<DashboardBlockContextValue>(() => ({
        blockId,
        mode,
        isModal: mode !== 'dashboard',
        isDashboardBlock: mode === 'dashboard' || mode === 'modal-view',
        isEditingModal: mode === 'modal-add' || mode === 'modal-edit' || mode === 'modal-copy',
        isViewModal: mode === 'modal-view',
        date,
        componentType,
    }), [blockId, mode, date, componentType]);
    
    return (
        <DashboardBlockContext.Provider value={value}>
            {children}
        </DashboardBlockContext.Provider>
    );
};

// ==================== ХУК ====================

/**
 * Хук для получения контекста блока дашборда.
 * 
 * @returns Контекст блока или null, если компонент не внутри провайдера
 */
export const useDashboardBlockContext = (): DashboardBlockContextValue | null => {
    return useContext(DashboardBlockContext);
};

/**
 * Хук для получения контекста блока с проверкой наличия.
 * Выбрасывает ошибку, если контекст не найден.
 * 
 * @returns Контекст блока
 * @throws Error если компонент не внутри DashboardBlockProvider
 */
export const useDashboardBlockContextRequired = (): DashboardBlockContextValue => {
    const context = useContext(DashboardBlockContext);
    
    if (!context) {
        throw new Error(
            'useDashboardBlockContextRequired must be used within a DashboardBlockProvider'
        );
    }
    
    return context;
};

/**
 * Хук для определения источника данных компонента.
 * 
 * Возвращает параметры, которые нужно передать в ReportTable/ReportChart
 * для корректной работы в разных контекстах.
 */
export const useBlockDataSource = () => {
    const context = useDashboardBlockContext();
    
    return useMemo(() => {
        // Если нет контекста — компонент используется вне дашборда
        if (!context) {
            return {
                blockId: undefined,
                isModal: false,
                displayMode: 'full' as const,
            };
        }
        
        const { blockId, mode, isModal, isDashboardBlock, isEditingModal, isViewModal } = context;
        
        // Определяем displayMode
        let displayMode: 'full' | 'compact' | 'preview';
        if (isEditingModal) {
            displayMode = 'preview';
        } else if (isDashboardBlock && !isViewModal) {
            displayMode = 'compact';
        } else {
            displayMode = 'full';
        }
        
        return {
            // Для модальных окон редактирования НЕ передаём blockId,
            // чтобы компоненты использовали глобальный/модальный state
            blockId: isEditingModal ? undefined : (blockId || undefined),
            isModal: isModal,
            displayMode,
            // Дополнительные флаги для компонентов
            isDashboardBlock,
            isEditingModal,
            isViewModal,
        };
    }, [context]);
};

export default DashboardBlockContext;
